/**
 * The Phase 4 operations: the account's sessions and security history, the
 * organization's identity provider, member roles and usage, findings triage,
 * incidents, traces and the platform description.
 *
 * Split from `operations.ts` for length and re-exported from it, as the
 * other phases are. One function per `/v1` operation, paths checked against
 * the generated contract.
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';
import { ApiError } from './problem';
import type { Paged } from './repo';

type Schemas = components['schemas'];

export type SessionInfo = Schemas['SessionInfo'];
export type AccountEvent = Schemas['AccountEvent'];
export type IdentityProvider = Schemas['IdentityProvider'];
export type IdentityProviderConfigure = Schemas['IdentityProviderConfigure'];
export type OrgRole = Schemas['Membership']['role'];
export type OrganizationUsage = Schemas['OrganizationUsage'];
export type UsageMeasure = Schemas['UsageMeasure'];
export type SecurityFinding = Schemas['SecurityFinding'];
export type SecurityFindingStatus = Schemas['SecurityFindingStatus'];
export type SecuritySeverity = Schemas['SecuritySeverity'];
export type Incident = Schemas['Incident'];
export type IncidentCreate = Schemas['IncidentCreate'];
export type IncidentState = Incident['state'];
export type IncidentSeverity = Incident['severity'];
export type CommitTrace = Schemas['CommitTrace'];
export type DeploymentChanges = Schemas['DeploymentChanges'];
export type TraceCoverage = Schemas['TraceCoverage'];
export type TracePullRequestCoverage = Schemas['TracePullRequestCoverage'];
export type TraceDeploymentCoverage = Schemas['TraceDeploymentCoverage'];
export type TraceCoverageMissing = Schemas['TraceCoverageMissing'];
export type EngineeringHealth = Schemas['EngineeringHealth'];
export type HealthDimension = Schemas['HealthDimension'];
export type PlatformMeta = Schemas['PlatformMeta'];

interface RepoScope {
  orgId: string;
  repoId: string;
}

const repoPath = ({ orgId, repoId }: RepoScope) => ({ org_id: orgId, repo_id: repoId });

function paged<T>(value: { data?: unknown; next_cursor?: unknown }): Paged<T> {
  return {
    data: (value.data ?? []) as T[],
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

/* ====================================================================== *
 * The account: sessions and security history (4A)
 * ====================================================================== */

export async function listSessions(): Promise<SessionInfo[]> {
  const value = await expect(http.GET('/users/me/sessions'));
  return paged<SessionInfo>(value).data;
}

export function revokeSession(sessionId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/users/me/sessions/{session_id}', {
      params: { path: { session_id: sessionId } },
    }),
  );
}

export function revokeOtherSessions(): Promise<void> {
  return expectNoContent(http.POST('/users/me/sessions/revoke-others'));
}

export async function listAccountSecurityEvents(): Promise<Paged<AccountEvent>> {
  const value = await expect(http.GET('/users/me/security-events'));
  return paged<AccountEvent>(value);
}

/* ====================================================================== *
 * The organization: identity provider, roles, usage (4A, 4B, 4N)
 * ====================================================================== */

/**
 * The provider, or null when the organization has none: the route answers
 * 404 for "not configured" as well as for "not yours", and from inside the
 * organization's own page the first is the only reading.
 */
export async function getIdentityProvider(orgId: string): Promise<IdentityProvider | null> {
  try {
    return await expect(
      http.GET('/organizations/{org_id}/identity-provider', {
        params: { path: { org_id: orgId } },
      }),
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export function configureIdentityProvider(
  orgId: string,
  body: IdentityProviderConfigure,
): Promise<IdentityProvider> {
  return expect(
    http.PUT('/organizations/{org_id}/identity-provider', {
      params: { path: { org_id: orgId } },
      body,
    }),
  );
}

export function removeIdentityProvider(orgId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/identity-provider', {
      params: { path: { org_id: orgId } },
    }),
  );
}

export function changeMemberRole(
  orgId: string,
  userId: string,
  role: OrgRole,
): Promise<Schemas['Membership']> {
  return expect(
    http.PATCH('/organizations/{org_id}/members/{user_id}', {
      params: { path: { org_id: orgId, user_id: userId } },
      body: { role },
    }),
  );
}

export function getOrganizationUsage(
  orgId: string,
  query: { since?: string; until?: string } = {},
): Promise<OrganizationUsage> {
  return expect(
    http.GET('/organizations/{org_id}/usage', {
      params: { path: { org_id: orgId }, query },
    }),
  );
}

/* ====================================================================== *
 * Findings (4C)
 * ====================================================================== */

export async function listRepositorySecurityFindings(
  scope: RepoScope,
  query: {
    status?: (SecurityFindingStatus | 'all')[];
    severity?: SecuritySeverity[];
    sort?: 'severity' | 'newest';
  } = {},
): Promise<Paged<SecurityFinding>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/security/findings', {
      params: { path: repoPath(scope), query },
    }),
  );
  return paged<SecurityFinding>(value);
}

export function triageSecurityFinding(
  scope: RepoScope,
  findingId: string,
  body: { acknowledge?: boolean; assignee_id?: string | null },
): Promise<SecurityFinding> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/security/findings/{finding_id}/triage',
      {
        params: { path: { ...repoPath(scope), finding_id: findingId } },
        body,
      },
    ),
  );
}

export function reopenSecurityFinding(
  scope: RepoScope,
  findingId: string,
): Promise<SecurityFinding> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/security/findings/{finding_id}/reopen',
      {
        params: { path: { ...repoPath(scope), finding_id: findingId } },
      },
    ),
  );
}

export function dismissSecurityFinding(
  scope: RepoScope,
  findingId: string,
  body: { reason: Schemas['SecurityDismissalReason']; note?: string; expires_at?: string },
): Promise<SecurityFinding> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/security/findings/{finding_id}/dismissal',
      {
        params: { path: { ...repoPath(scope), finding_id: findingId } },
        body,
      },
    ),
  );
}

/* ====================================================================== *
 * Incidents and traces (4J, 4K)
 * ====================================================================== */

export async function listIncidents(
  scope: RepoScope,
  query: { state?: IncidentState; live?: boolean } = {},
): Promise<Paged<Incident>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/incidents', {
      params: { path: repoPath(scope), query },
    }),
  );
  return paged<Incident>(value);
}

export function createIncident(scope: RepoScope, body: IncidentCreate): Promise<Incident> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/incidents', {
      params: { path: repoPath(scope) },
      body,
    }),
  );
}

export function transitionIncident(
  scope: RepoScope,
  number: number,
  body: { state: IncidentState; note?: string; resolution?: string; if_version?: number },
): Promise<Incident> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/incidents/{number}/transition', {
      params: { path: { ...repoPath(scope), number } },
      body,
    }),
  );
}

export type IncidentUpdate = Schemas['IncidentUpdate'];
export type IncidentLinks = Schemas['IncidentLinks'];

/** One incident by number (observability, DL-245): the detail page's read. */
export function getIncident(scope: RepoScope, number: number): Promise<Incident> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/incidents/{number}', {
      params: { path: { ...repoPath(scope), number } },
    }),
  );
}

/**
 * Change an incident's fields, links and service attribution. `service_id`
 * null clears the attribution; `if_version` refuses a stale edit (412).
 */
export function updateIncident(
  scope: RepoScope,
  number: number,
  body: IncidentUpdate,
): Promise<Incident> {
  return expect(
    http.PATCH('/organizations/{org_id}/repositories/{repo_id}/incidents/{number}', {
      params: { path: { ...repoPath(scope), number } },
      body,
    }),
  );
}

export function traceCommit(scope: RepoScope, sha: string): Promise<CommitTrace> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/trace/commits/{sha}', {
      params: { path: { ...repoPath(scope), sha } },
    }),
  );
}

export function getDeploymentChanges(
  scope: RepoScope,
  deploymentId: string,
): Promise<DeploymentChanges> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/deployments/{deployment_id}/changes', {
      params: { path: { ...repoPath(scope), deployment_id: deploymentId } },
    }),
  );
}

/** The query of a coverage read (DL-221). Times are RFC 3339. */
export interface CoverageQuery {
  since?: string;
  until?: string;
  key_prefix?: string[];
  limit?: number;
}

export function getTraceCoverage(
  scope: RepoScope,
  query: CoverageQuery = {},
): Promise<TraceCoverage> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/trace/coverage', {
      params: { path: repoPath(scope), query },
    }),
  );
}

/* ====================================================================== *
 * The platform (4L)
 * ====================================================================== */

export function getMeta(): Promise<PlatformMeta> {
  return expect(http.GET('/meta'));
}
